/** @typedef {Record<string, string | Record<string, unknown>>} LocaleTree */
/** @type {LocaleTree} */
const en = {
  app: {
    defaultDocTitle: "peplies consult - Sports Marketing Consultants",
  },
  nav: {
    about: "About Us",
    hallOfFame: "Hall of Fame",
    services: "Services",
    servicesAthletes: "Services for Athletes",
    servicesCompanies: "Services for Companies",
    references: "References",
    refAthletes: "Athlete References",
    refCompanies: "Company References",
    press: "Press",
    campaigns: "Campaigns",
    campaignsPrint: "PR | Print | Poster",
    campaignsTv: "TV Commercials",
    publications: "Publications",
    scientificAdvisoryBoard: "Scientific Advisory Board",
    toggleMenu: "Toggle navigation menu",
  },
  auth: {
    login: "Login",
    logout: "Logout",
    password: "Password",
    passwordPlaceholder: "Enter password",
    wrongPassword: "Incorrect password",
    showPassword: "Show password",
    hidePassword: "Hide password",
    notice: "This client-side login only protects navigation visibility and is not a secure authentication system.",
    unavailable: "Login is unavailable because no password is configured.",
  },
  footer: {
    linksHeading: "LINKS",
    about: "About",
    services: "Services",
    contact: "Contact",
    legal: "Legal Notice",
    privacy: "Privacy Policy",
    servicesBlockHeading: "SERVICES",
    servicesAthletes: "Services for Athletes",
    servicesCompanies: "Services for Companies",
    rights: "All rights reserved.",
    companyName: "peplies consult GmbH",
    companyStreet: "Höhenstraße 12",
    companyPostalCode: "65321",
    companyCity: "Heidenrod",
    copyrightBrand: "peplies consult - Sports Marketing Consultants",
  },
  intro: {
    title: "About Us",
    p1: "We are the 'Consultants of Choice' for national and international athletes, clubs, associations, and businesses in the field of sports marketing and sports sponsorship.",
    p2: "As a sister company of peplies consult - Marketing Consultants and peplies consult - Executive Consultants, and as a cooperation partner of leading global sports economics, sports law, and sports medicine university faculties, we are committed to both practice and science.",
    p3: "We measure ourselves by the satisfaction of our clients and our contribution to the economy, sports, and society.",
    contactCta: "Contact us",
  },
  servicesSection: {
    title: "Services",
    subtitle: "Our Services at a Glance.",
    athletesTitle: "Services for Athletes",
    athletesBody:
      'For our athletes, we provide - also with the involvement of associated partners and networks - all measures of holistic care at the highest qualitative level ("18-point program"). Far-reaching international references exist especially in all areas of winter sports (Alpine skiing, biathlon, ski jumping, Nordic combined, bobsleigh, luge) as well as within the sports of tennis, golf, equestrian and athletics.',
    companiesTitle: "Services for Companies",
    companiesBody:
      "For companies already engaged in sports sponsorship, we offer measures for analyzing, controlling, and optimizing their sponsorship concepts and corresponding projects, as well as project-supporting measures; for companies planning to enter the field of sports sponsorship, we offer market research and concept development. Our services include: sports sponsorship concepts, market research & controlling, project-supporting measures, and scouting and acquisition of athletes.",
  },
  clients: {
    title: "Companies — Sports Rights Marketing / Sports Sponsorship",
  },
  contact: {
    docTitle: "peplies consult - Send us a message",
    modalTitle: "Contact",
    logoAlt: "peplies consult",
    close: "Close",
    callUs: "Call us:",
    writeUs: "Write to us:",
    mailUs: "Email us:",
    country: "Germany",
    companyName: "peplies consult GmbH",
    street: "Höhenstraße 12",
    postalCode: "65321",
    city: "Heidenrod",
  },
  privacy: {
    docTitle: "peplies consult - Privacy Policy",
    title: "Privacy Policy",
  },
  legal: {
    docTitle: "peplies consult - Legal Notice",
    title: "Legal Notice",
    tmgaHeading: "Information pursuant to § 5 TMG",
    country: "Germany",
    contact: "Contact",
    vatHeading: "Tax number / VAT identification",
    taxNumberLine: "Tax number (Steuernummer) 043 241 09523, Tax Office (Finanzamt) Wiesbaden II",
    vatIntro: "VAT identification number pursuant to §27 a of the German VAT Act:",
    managingDirector: "Managing partner",
    registerHeading: "Commercial register entry",
    registerCourtLabel: "Register court:",
    registerCourt: "Local Court Wiesbaden (Amtsgericht Wiesbaden)",
    registerNumberLabel: "Registry number:",
    responsibleHeading: "Responsible for content in accordance with Section 18 of the German Media Services Treaty (MStV):",
  },
  athletesPage: {
    docTitle: "peplies consult - Athletes",
    title: "Current Athletes",
    subtitle: "Athletes for whom peplies consult GmbH has or had marketing responsibility:",
  },
  athleteDetail: {
    docTitle: "peplies consult - Sports Marketing Consultants",
    notFound: "Athlete not found",
    birthday: "Birthday:",
    discipline: "Sport Discipline:",
    club: "Club:",
    interests: "Interests:",
    profession: "Profession:",
    hobby: "Hobby:",
    achievements: "Achievements:",
    photoCredit: "Photo: ",
  },
  companyReferences: {
    docTitle: "peplies consult - Companies — Sports Rights Marketing / Sports Sponsorship",
    title: "Companies — Sports Rights Marketing / Sports Sponsorship",
    pageIntro: "A selection of companies that work with and have worked with peplies consult GmbH:",
    reference: "Reference",
  },
  hallOfFame: {
    docTitle: "peplies consult - Hall of Fame",
    title: "Hall of Fame",
    subtitle:
      '"Sports legends" or projects for which peplies consult GmbH had or has marketing responsibility:',
    achievementsHeading: "Career highlights",
    noAchievements: "No achievements on file.",
    flipHint: "Hover or tap to flip",
  },
  press: {
    docTitle: "peplies consult - Press",
    title: "Press",
    pageIntro: "Selected media coverage about peplies consult GmbH:",
    openHint: "Click to open",
    unavailable: "This document is currently unavailable.",
    categories: {
      olympics: "Olympics",
      magazine: "Magazine",
      news: "News",
    },
  },
  publications: {
    docTitle: "peplies consult - Publications",
    title: "Publications",
    pageIntro: "Publications to which peplies consult GmbH contributed:",
    isbn: "ISBN",
    openPdf: "Open PDF",
    openImage: "Enlarge image",
    empty: "Publication content is currently being prepared.",
  },
  scientificAdvisoryBoard: {
    docTitle: "peplies consult - Scientific Advisory Board",
    title: "Scientific Advisory Board",
    sections: {
      sportsEconomics: {
        title: "Sports Economics",
        members: [
          "Prof. Dr. Herbert Woratschek University of Bayreuth",
          "Prof. Dr. Michael Wehrheim (deceased) University of Marburg, European Business School (EBS) Oestrich-Winkel",
          "Prof. Dr. Guido Schafmeister Macromedia University of Media and Communication Munich",
        ],
      },
      sportsMedicine: {
        title: "Sports Medicine/Sports Science/Training Theory/Complementary Medicine",
        members: [
          "Prof. Dr. Dieter Lazik University of Potsdam, University of Luxembourg",
          "Prof. Dr. Hartmut Schröder Viadrina University, Frankfurt/Oder",
          "Dr. Inge Gerding Bad Wildungen",
        ],
      },
    },
  },
  printCampaign: {
    docTitle: "peplies consult - PR, Print, Poster",
    title: "PR | Print | Posters",
    empty: "Campaign material will be published here soon.",
    galleryIntro:
      "A selection of classic communication campaigns planned and implemented by peplies consult GmbH:",
    imageSectionTitle: "Image Campaigns",
    pdfSectionTitle: "PDF Campaigns",
    items: "items",
    openImage: "Open image:",
    openPdf: "Open PDF:",
    openPdfCta: "Open PDF in a new tab",
  },
  tv: {
    docTitle: "peplies consult - TV Commercials",
    title: "TV Commercials",
    subtitle: "A selection of TV campaigns planned and implemented by peplies consult GmbH:",
    videoNotSupported: "Your browser does not support the video tag.",
  },
  athleteServices: {
    docTitle: "peplies consult - Services for Athletes",
    title: "Services for Athletes",
    intro:
      'For our athletes, we provide all measures of holistic care at the highest quality level ("18-point program") - also in cooperation with associated partners and networks. Extensive international references exist primarily in all areas of winter sports (Alpine skiing, biathlon, ski jumping, Nordic combined, bobsleigh, luge) as well as in tennis, golf, equestrian sports, and athletics.',
  },
  companyServices: {
    docTitle: "peplies consult - Services for Companies",
    title: "Services for Companies",
  },
  cookie: {
    message:
      "We store settings in your browser (local storage). If you choose “Accept all”, videos are loaded from Cloudinary (USA); your IP address may be transmitted. See our privacy policy for details.",
    acceptAll: "Accept all",
    acceptNecessary: "Necessary only",
    decline: "Decline",
    reopenSettings: "Change cookie settings",
    externalMediaBlocked:
      "External videos are disabled. Use “Change cookie settings” and choose “Accept all”, or read the privacy policy.",
  },
  language: {
    label: "Language",
    names: {
      de: "Deutsch",
      en: "English",
      fr: "Français",
      es: "Español",
    },
  },
  notFound: {
    docTitle: "peplies consult - Page not found",
    message: "The requested page could not be found.",
    home: "Back to home",
  },
};

export default en;
